import { runInAction, makeAutoObservable } from "mobx"

import { BaseStore } from "stores/base_store"
import ProjectAPI from "api/project"
import { api_get } from "api/utils"
import { api_post } from 'api/utils';

export type CollabInviteProps = {
    key: string;
    cls: string;
    uuid: string;
    collab: string;
    associated_type: string;
    associated_uuid: string;
    variant: string;
    identity: string;
    status: string;
    message: string;
    invited_by?: string;
    date_created: string;
    date_updated: string;
    updateProps?: (attributes: CollabInviteProps) => void;
};


// Domain object CollabInvite
export class CollabInvite implements CollabInviteProps {
    key = "";
    cls = "";
    uuid = "";
    collab = "";
    associated_type = "";
    associated_uuid = "";
    variant = "";
    identity = "";
    status = "";
    message = "";
    invited_by = "";
    date_created = "";
    date_updated = "";

    constructor(attributes: CollabInviteProps) {
        makeAutoObservable(this, {
            updateProps: false
        })
        if (attributes) {
            this.updateProps(attributes);
        }
    }


    updateProps(attributes: CollabInviteProps) {
        runInAction(() => {
            Object.assign(this, attributes);
        });
    }
}


export class CollabInviteStore extends BaseStore<CollabInviteProps, CollabInvite> {

    async fetchList(collabKey: string, cursor = "") {
        ProjectAPI.collabInviteList(collabKey, cursor).then((resp) => {
            this.receiveUpdate(resp.items);
            if (resp.cursor) {
                this.fetchList(collabKey, resp.cursor);
            }
            return resp.items;
        })
    }

    async fetchCollabInvite(workKey: string, invite: string) {
        const params = { identity: workKey, invite };
        return api_get("/collab/invite", params)
            .then(async (res) => {
                if (res.ok) {
                    const data = await res.json();
                    this.receiveUpdate(data);
                    return data;
                }
                else {
                    console.error("error fetching invite", res);
                    return null;
                }
            });
    }

    async fetchAvailableList(workKey: string, cursor = "") {
        return ProjectAPI.collabInviteAvailableList(workKey, cursor).then((resp) => {
            this.receiveUpdate(resp.items);
            if (resp.cursor) {
                this.fetchAvailableList(workKey, resp.cursor);
            }
            return resp.items;
        })
    }

    async fetchMutualList(identityKey: string, cursor = "") {
        ProjectAPI.collabInviteMutualList(identityKey, cursor).then((resp) => {
            this.receiveUpdate(resp.items);
            if (resp.cursor) {
                this.fetchMutualList(identityKey, resp.cursor);
            }
            return resp.items;
        })
    }

    async fetchAcceptedList(cursor = "") {
        ProjectAPI.collabInviteAcceptedList(cursor).then((resp) => {
            this.receiveUpdate(resp.items);
            if (resp.cursor) {
                this.fetchAcceptedList(resp.cursor);
            }
            return resp.items;
        })
    }

    async doAcceptCollabInvite(invite: string, identity: string) {
        console.log("doAcceptCollabInvite %s %s", invite, identity);
        return ProjectAPI.collabAcceptInvite(invite, identity)
            .then(({ invite }: { invite: CollabInvite }) => {
                console.log("accepted %o", invite);
                this.receiveUpdate(invite);
                return invite as CollabInvite;
            })
    }

    async doWithdrawCollabInvite(invite: string, collab: string, identityKey: string) {
        return ProjectAPI.collabWithdrawInvite(invite, collab, identityKey).then((data : CollabInvite) => {
            this.receiveUpdate(data);
            return data as CollabInvite;
        })
    }

    async create(collab: string, identityKey: string) {
        return ProjectAPI.collabInviteCreate(collab, identityKey).then((data) => {
            this.receiveUpdate(data);
            return data as CollabInvite;
        })
    }

    async supportJoinCollab(workKey: string, collabKey: string) {
        return api_post('/collab/invite/support-join', { identity: workKey, collab: collabKey })
            .then(async (res) => {
                const data = await res.json();
                this.receiveUpdate(data);
                return data as CollabInvite;
            }
        );
    }

    async inviteMutual(collab: string, identityKey: string, mutualIdentityKey: string) {
        return ProjectAPI.collabInviteMutual(collab, identityKey, mutualIdentityKey)
            .then((data) => {
                this.receiveUpdate(data);
                return data as CollabInvite;
            })
    }


    async inviteDedicated(collab: string, identityKey: string) {
        return ProjectAPI.collabInviteDedicated(collab, identityKey)
            .then((data) => {
                this.receiveUpdate(data);
                return data as CollabInvite;
            })
    }

    async inviteDedicatedSpecific(workKey: string, collab: string, assistant_identity: string) {
        return api_post('/collab/invite/dedicated-specific', { identity: workKey, collab, assistant_identity })
            .then(async (res) => {
                const data = await res.json();
                this.receiveUpdate(data);
                return data as CollabInvite;
            }
            );
    }

    async inviteSupport(collab: string, identityKey: string) {
        return ProjectAPI.collabInviteSupport(collab, identityKey)
            .then((data) => {
                this.receiveUpdate(data);
                return data as CollabInvite;
            })
    }

    async inviteTeamMember(workKey:string, associated_type: string, associated_uuid: string, identityKey: string, message: string) {
        return api_post('/collab/invite/team-member',
            { identity: workKey, associated_type, associated_uuid, team_member: identityKey, message })
            .then(async (res) => {
                const data = await res.json();
                this.receiveUpdate(data);
                return data as CollabInvite;
            });
    }


    async dedicatedInviteDayOne(collab: string, identityKey: string) {
        return ProjectAPI.collabDedicatedInviteDayOne(collab, identityKey)
            .then((data) => {
                this.receiveUpdate(data);
                return data as CollabInvite;
            })
    }


    getMutualInvites(identityKey : string) {
        return this.items.filter(invite => invite.identity === identityKey).sort((a, b) => {
            return a.date_created > b.date_created ? 1 : -1;
        });
    }

    getInvitesForCollab(collabKey : string) {
        return this.items.filter(invite => invite.collab === collabKey).sort((a, b) => {
            return a.date_created > b.date_created ? 1 : -1;
        });
    }

    getInvitesByStatus(status: string, variant: string="collab") {
        return this.items.filter(invite => invite.status === status && invite.variant === variant)
            .sort((a, b) => {
            return a.date_created > b.date_created ? 1 : -1;
        });
    }

    modelConstructor() {
        return CollabInvite;
    }

}
